import { bestPrices, flashFor, pricedBelowMin, type Flash, type OutcomeBest } from "@/lib/odds-screen/live/derive";
import type { BoardRow, BookId, EventHeader, MarketQuote, MarketSpec, OddsDisplay } from "@/lib/odds-screen/live/types";
import { cellLineLabel, outcomeDisplayOrder, outcomeLine, outcomeSideLabel, type RowLine } from "./labels";

export type CellSide = {
  readonly outcome: number;
  readonly lineLabel: string;
  readonly best: OutcomeBest | null;
  readonly belowMin: boolean;
  readonly display: string;
  readonly bestBook: BookId | null;
  readonly ties: number;
  readonly sideLabel: string;
};

export type MarketCells = {
  readonly spec: MarketSpec;
  readonly main: number | null;
  readonly sides: readonly CellSide[];
};

export function marketCells(
  quote: MarketQuote | null,
  spec: MarketSpec,
  event: EventHeader | undefined,
  selected: ReadonlySet<BookId>,
  format: OddsDisplay,
  minStake: number,
): MarketCells | null {
  if (quote === null) return null;
  const best = bestPrices(quote, spec, selected, format, minStake);
  const sides = outcomeDisplayOrder(spec).map((outcome): CellSide => {
    const winner = best[outcome];
    return {
      outcome,
      lineLabel: cellLineLabel(spec, quote.main, outcome),
      best: winner,
      belowMin: winner === null && pricedBelowMin(quote, outcome, selected, minStake),
      display: winner ? winner.display : "",
      bestBook: winner ? winner.books[0] ?? null : null,
      ties: winner ? winner.books.length - 1 : 0,
      sideLabel: outcomeSideLabel(spec, quote.main, event, outcome),
    };
  });
  return { spec, main: quote.main, sides };
}

export function rowCells(
  row: BoardRow,
  specs: readonly MarketSpec[],
  event: EventHeader | undefined,
  selected: ReadonlySet<BookId>,
  format: OddsDisplay,
  minStake: number,
): readonly (MarketCells | null)[] {
  return specs.map((spec, slot) => marketCells(row.markets[slot] ?? null, spec, event, selected, format, minStake));
}

export function lineSides(market: MarketCells, lines: readonly RowLine[]): readonly (number | null)[] {
  const placed = market.sides.map((side, position) => outcomeLine(market.spec, side.outcome) ?? lines[position]);
  return lines.map((line) => {
    const index = placed.indexOf(line);
    return index === -1 ? null : index;
  });
}

export function flashCells(
  prev: readonly (MarketCells | null)[],
  next: readonly (MarketCells | null)[],
): readonly (readonly Flash[])[] {
  return next.map((market, slot) => {
    if (market === null) return [];
    const before = prev[slot];
    return market.sides.map((side, index) => {
      const priorSide = before?.sides[index];
      return flashFor(priorSide?.best ?? null, side.best, before?.main ?? null, market.main);
    });
  });
}
